<template>
    <div id="sidebar" class="sidebar" :class="{ 'mobile-open': isMobileOpen, 'sidebar-closed': isDesktopClosed }">
        <div id="sidebar-menu" class="sidebar-menu">
            <div class="sidebar-logo-container">
                <img
                    src="/images/PalazzoBuilding.png"
                    alt="Brittany Palazzo"
                    class="sidebar-logo"
                    @error="logoError = true"
                    v-show="!logoError"
                >
                <div v-if="logoError" class="logo-error-fallback">
                    <span class="material-symbols-outlined">image</span>
                    <span>Logo unavailable</span>
                </div>
            </div>
            <ul class="nav flex-column">
                <li :class="currentPath === 'Dashboard' ? 'active' : ''">
                    <router-link to="/dashboard" @click="onNavClick">
                        <span class="material-symbols-outlined">dashboard</span>
                        <span>Dashboard</span>
                    </router-link>
                </li>
                <li :class="['Clients','AddClients','EditClients','ViewClients'].includes(currentPath) ? 'active' : ''">
                    <router-link to="/clients" @click="onNavClick">
                        <span class="material-symbols-outlined">group</span>
                        <span>Clients</span>
                    </router-link>
                </li>
                <li :class="['Events','AddEvents','EditEvents','ViewEvents'].includes(currentPath) ? 'active' : ''">
                    <router-link to="/events" @click="onNavClick">
                        <span class="material-symbols-outlined">event</span>
                        <span>Events</span>
                    </router-link>
                </li>
                <li :class="['Billings','AddBillings','EditBillings','ViewBillings'].includes(currentPath) ? 'active' : ''">
                    <router-link to="/billings" @click="onNavClick">
                        <span class="material-symbols-outlined">payments</span>
                        <span>Billings</span>
                    </router-link>
                </li>
                <li :class="currentPath === 'Calendar' ? 'active' : ''">
                    <router-link to="/calendar" @click="onNavClick">
                        <span class="material-symbols-outlined">calendar_month</span>
                        <span>Calendar</span>
                    </router-link>
                </li>
                <li class="menu-title mt-3">
                    <span>Reports</span>
                </li>
                <li v-if="isAdmin || isSales || isAccounting" :class="currentPath === 'ClientsReport' ? 'active' : ''">
                    <router-link to="/clientsreport" @click="onNavClick">
                        <span class="material-symbols-outlined">people</span>
                        <span>Clients Report</span>
                    </router-link>
                </li>
                <li :class="currentPath === 'EventsReport' ? 'active' : ''">
                    <router-link to="/eventsreport" @click="onNavClick">
                        <span class="material-symbols-outlined">summarize</span>
                        <span>Events Report</span>
                    </router-link>
                </li>
                <li v-if="isAdmin || isSales || isAccounting" :class="currentPath === 'SalesReport' ? 'active' : ''">
                    <router-link to="/salesreport" @click="onNavClick">
                        <span class="material-symbols-outlined">trending_up</span>
                        <span>Sales Report</span>
                    </router-link>
                </li>
                <li v-if="isAdmin || isSales || isAccounting" :class="currentPath === 'TransactionsReport' ? 'active' : ''">
                    <router-link to="/transactionsreport" @click="onNavClick">
                        <span class="material-symbols-outlined">swap_horiz</span>
                        <span>Transactions Report</span>
                    </router-link>
                </li>
                <li v-if="isAdmin" :class="currentPath === 'LoginHistory' ? 'active' : ''">
                    <router-link to="/login-history" @click="onNavClick">
                        <span class="material-symbols-outlined">history</span>
                        <span>Login Report</span>
                    </router-link>
                </li>
                <li v-if="isAdmin" class="menu-title mt-3">
                    <span>Admin Only</span>
                </li>
                <li v-if="isAdmin" :class="['User','EditUser','ViewUser','AddUser','AddUsers'].includes(currentPath) ? 'active' : ''">
                    <router-link to="/users" @click="onNavClick">
                        <span class="material-symbols-outlined">admin_panel_settings</span>
                        <span>Users</span>
                    </router-link>
                </li>
                <li v-if="isAdmin" class="menu-title mt-3">
                    <span>Audit</span>
                </li>
                <li v-if="isAdmin" :class="currentPath === 'AuditLogs' ? 'active' : ''">
                    <router-link to="/audit-logs" @click="onNavClick">
                        <span class="material-symbols-outlined">receipt_long</span>
                        <span>Audit Logs</span>
                    </router-link>
                </li>
                <li v-if="isAdmin" :class="currentPath === 'AuditAlerts' ? 'active' : ''">
                    <router-link to="/audit-alerts" @click="onNavClick">
                        <span class="material-symbols-outlined">notifications</span>
                        <span>Audit Alerts</span>
                    </router-link>
                </li>
                <li v-if="isAdmin" :class="currentPath === 'AuditCompliance' ? 'active' : ''">
                    <router-link to="/audit-compliance" @click="onNavClick">
                        <span class="material-symbols-outlined">verified</span>
                        <span>Compliance</span>
                    </router-link>
                </li>
            </ul>
            <div class="sidebar-version" v-if="version">
                <span class="sidebar-version-value">{{ version }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import { ROLES } from '../constants/roles';
export default {
    props: {
        role: Number,
        isOpen: {
            type: Boolean,
            default: false
        },
        isDesktopClosed: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isAdmin: null,
            isSales: null,
            isAccounting: null,
            currentPath: null,
            name: '',
            version: '',
            userMenuOpen: false,
            logoError: false,
        }
    },
    computed: {
        isMobileOpen() {
            return this.isOpen
        }
    },
    mounted() {
        const role = parseInt(localStorage.getItem('role'))
        this.isAdmin = role === ROLES.ADMIN
        this.isSales = role === ROLES.SALES
        this.isAccounting = role === ROLES.ACCOUNTING
        this.currentPath = this.$route?.name || null
        this.getUser()
        this.fetchVersion()
    },
    methods: {
        fetchVersion() {
            fetch('/version.json')
                .then((response) => response.json())
                .then((data) => {
                    this.version = data.version || ''
                })
                .catch(() => {})
        },
        getUser() {
            axios.get('/api/get_token')
                .then((response) => { this.name = response.data.name })
                .catch((error) => {
                    if (error.response && (error.response.status == 401 || error.response.status == 403)) {
                        this.$router.push({ name: "Login" })
                    }
                    this.$store.commit("showToast", { message: "Session error. Please re-login." })
                })
        },
        onNavClick() {
            this.$emit('close')
        }
    },
    watch: {
        $route(to) {
            this.currentPath = to.name
            const role = parseInt(localStorage.getItem('role'))
            this.isAdmin = role === ROLES.ADMIN
            this.isSales = role === ROLES.SALES
            this.isAccounting = role === ROLES.ACCOUNTING
        },
        role(newRole) {
            const role = parseInt(newRole)
            this.isAdmin = role === ROLES.ADMIN
            this.isSales = role === ROLES.SALES
            this.isAccounting = role === ROLES.ACCOUNTING
        }
    }
}
</script>

<style scoped>
.sidebar-menu {
    flex: 1 1 auto;
    display: grid;
    grid-template-rows: auto 1fr auto;
    min-height: 0;
    height: 100%;
}

.sidebar-menu .nav.flex-column {
    grid-row: 2;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    min-width: 0;
    margin: 0;
    padding: 12px 0;
}

.sidebar-version {
    grid-row: 3;
    padding: 14px 20px calc(14px + env(safe-area-inset-bottom, 0));
    border-top: 1px solid rgba(128, 117, 105, 0.18);
    background: rgba(255, 248, 243, 0.92);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
}

.sidebar-version-value {
    font-family: 'Manrope', sans-serif;
    font-size: 0.8125rem;
    font-weight: 400;
    color: var(--color-text-muted, #807569);
    word-break: break-word;
}

.sidebar-logo-container {
    grid-row: 1;
    padding: 24px 20px;
    text-align: center;
}

.sidebar-logo {
    max-width: 100%;
    max-height: 100px;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
    margin: 0 auto;
}

.logo-error-fallback {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: var(--color-text-muted, #807569);
    font-size: 12px;
}

.logo-error-fallback .material-symbols-outlined {
    font-size: 32px;
    margin-bottom: 8px;
    color: var(--color-outline, #807569);
}

.sidebar-cta {
    grid-row: 3;
    padding: 0 16px 16px;
}

.cta-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 12px 20px;
    border-radius: 9999px;
    background: linear-gradient(135deg, var(--color-primary, #775928), var(--color-primary-light, #b08d57));
    color: #fff;
    font-family: 'Manrope', sans-serif;
    font-weight: 600;
    font-size: 0.875rem;
    text-decoration: none;
    transition: all 150ms ease;
    box-shadow: 0px 4px 12px rgba(119, 89, 40, 0.25);
}

.cta-button:hover {
    transform: translateY(-1px);
    box-shadow: 0px 6px 16px rgba(119, 89, 40, 0.35);
    color: #fff;
}

.cta-button .material-symbols-outlined {
    font-size: 20px;
}

@media (max-width: 768px) {
    .sidebar-logo-container {
        padding: 16px 12px;
    }

    .sidebar-logo {
        max-height: 80px;
    }

    .sidebar-menu li a {
        min-height: 48px;
        display: flex;
        align-items: center;
    }

    .sidebar-version {
        padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0));
    }
}

@media (max-width: 480px) {
    .sidebar-logo-container {
        padding: 12px 8px;
    }

    .sidebar-logo {
        max-height: 64px;
    }
}

@media (min-width: 1200px) {
    .sidebar-logo {
        max-height: 120px;
    }
}
</style>
